import { useTranslation } from 'react-i18next';
import type { AiFieldKey, QuickTaskProposal } from '../types';
import type { QuickTaskDraftFormValues } from '../schemas';

interface ProposalNewEntityWarningProps {
  proposal: QuickTaskProposal;
  /** The values on screen — what the user is actually confirming, edits included. */
  draft: QuickTaskDraftFormValues;
}

/**
 * Only the kinds confirming can actually create. The assignee was listed here, but apply sends
 * it as a plain id and creates nobody — announcing "the member X will be created for the whole
 * board" was a promise nothing kept.
 */
const NEW_ENTITY_KIND: Partial<Record<AiFieldKey, string>> = {
  statusId: 'status',
  groupId: 'group',
  tags: 'tag',
};

/**
 * AMF-6: what confirming would add to the whole board, named one by one.
 *
 * Tags come from the proposal's list, not from `fields.tags`: that entry summarises several
 * values into one, so a proposal naming two new tags warned about whichever happened to be first.
 */
export function ProposalNewEntityWarning({ proposal, draft }: ProposalNewEntityWarningProps) {
  const { t } = useTranslation('ai');

  const entities: { key: string; kind: string; name: string }[] = [];

  for (const [key, field] of Object.entries(proposal.fields)) {
    if (key === 'tags' || !field.isNew || !field.value) continue;
    // Only while the draft still holds it. The user can pick something the board already has,
    // and then nothing is created — the payload stopped creating it, so the warning must stop
    // announcing it to the whole board.
    if (draft[key as keyof typeof draft] !== field.value) continue;
    const kind = NEW_ENTITY_KIND[key as AiFieldKey];
    if (!kind) continue;
    entities.push({ key, kind, name: field.value });
  }

  const draftTags = draft.tags ?? [];
  for (const tag of proposal.tags) {
    if (!tag.isNew || !draftTags.includes(tag.name)) continue;
    entities.push({ key: `tag:${tag.name}`, kind: 'tag', name: tag.name });
  }

  if (entities.length === 0) return null;

  return (
    <div
      data-testid="ai-proposal-new-entity-warning"
      role="alert"
      className="mt-3 rounded-md border border-warning/40 bg-warning/10 p-2 text-xs"
    >
      {entities.map(({ key, kind, name }) => (
        <p key={key}>
          {t('quickCreate.newEntityWarning', {
            kind: t(`quickCreate.newEntityKinds.${kind}`),
            name,
          })}
        </p>
      ))}
    </div>
  );
}
